/**
 * Header do app, igual em todas as telas (Metronic e Ops-Next): uma linha de
 * 48px com três zonas sempre no mesmo lugar.
 *
 * 1. Breadcrumb: onde a pessoa está (menu lateral + o que a tela declara).
 * 2. "Ir para…" (⌘K / Ctrl K): paleta de navegação de qualquer tela.
 * 3. Global: o sino de notificações e o nome e menu da pessoa.
 *
 * Ações e abas das telas nunca vêm para cá: ficam na barra da página
 * (`PageBar`).
 */
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import clsx from 'clsx';

import {useAuth} from 'app/modules/auth';
import useRouteRedirection from 'app/shared/hooks/useRouteRedirection';

import {toAbsoluteUrl} from '../../../helpers';
import {useAppMenu} from '../aside/appMenu';
import Breadcrumb from './Breadcrumb';
import {resolveSection} from './breadcrumbTrail';
import CommandPalette from './CommandPalette';
import NotificationBell from './notifications/NotificationBell';
import {pushRecentPage} from './recentPages';
import {
  isMacPlatform,
  isPaletteShortcut,
  shortcutAria,
  shortcutLabel,
} from './shortcut';
import {useBreadcrumbTrail} from './useBreadcrumbTrail';
import UserMenu from './UserMenu';
import './appHeader.scss';

/**
 * Outro diálogo modal aberto (modal do Bootstrap): ele prende o foco, e a
 * paleta por cima ficaria sem teclado. O atalho espera ele fechar.
 */
const otherModalOpen = () =>
  Boolean(document.querySelector('.modal.show, .modal[aria-modal="true"]'));

const AppHeader: FC<{asideCollapsed: boolean}> = ({asideCollapsed}) => {
  const {pathname} = useLocation();
  const {currentUser} = useAuth();
  const menu = useAppMenu();
  const trail = useBreadcrumbTrail();
  const mac = useMemo(() => isMacPlatform(), []);
  const {routeRedirectionByUser} = useRouteRedirection();

  // Recentes da paleta: a página com o nome que o breadcrumb mostra, quando
  // ele já carregou e a página é de uma seção do menu da pessoa
  const userUid = currentUser?.uid || '';
  const current = trail[trail.length - 1];
  // Contexto: os níveis de antes ("Fornecedores / Rede Aurora")
  const sectionTitle =
    trail.length > 1
      ? trail
          .slice(0, -1)
          .map(({title}) => title)
          .join(' / ')
      : undefined;
  const ready = trail.length > 0 && !trail.some(({loading}) => loading);
  useEffect(() => {
    if (!ready || !userUid || !current?.title) return;
    if (!resolveSection(pathname, menu)) return;
    pushRecentPage(userUid, {
      path: pathname,
      title: current.title,
      section: sectionTitle,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, userUid, pathname, current?.title, sectionTitle]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);

  const openPalette = useCallback(() => {
    returnFocus.current = document.activeElement as HTMLElement | null;
    setPaletteOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setPaletteOpen(false);
    const target = returnFocus.current;
    returnFocus.current = null;
    if (target && document.contains(target)) target.focus();
  }, []);

  // ⌘K / Ctrl K de qualquer tela, mesmo com o foco num campo de texto (fase
  // de captura: chega antes dos campos). De novo com a paleta aberta, fecha.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event, mac)) return;
      if (!paletteOpen && otherModalOpen()) return;
      event.preventDefault();
      if (paletteOpen) closePalette();
      else openPalette();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [mac, paletteOpen, openPalette, closePalette]);

  return (
    <header
      id='kt_header'
      className={clsx(
        'opx-app opx-appbar',
        asideCollapsed && 'is-aside-collapsed'
      )}
    >
      {/* Aside no celular/tablet (drawer do Metronic, pelo id) */}
      <button
        type='button'
        id='kt_aside_toggle'
        className='opx-appbar__iconbtn d-lg-none'
        aria-label='Abrir menu'
      >
        <i className='bi bi-list' aria-hidden='true' />
      </button>
      {/* O símbolo do menu lateral recolhido (o logo antigo era branco) */}
      <Link
        to={routeRedirectionByUser('/quotes/workspace')}
        className='opx-appbar__logo d-lg-none'
      >
        <img
          src={toAbsoluteUrl('/media/logos/mecanizou-logo-symbol.svg')}
          alt='Mecanizou'
        />
      </Link>

      <Breadcrumb trail={trail} />

      <div className='opx-appbar__end'>
        <button
          type='button'
          className='opx-appbar__goto'
          aria-label='Ir para…'
          aria-haspopup='dialog'
          aria-keyshortcuts={shortcutAria(mac)}
          onClick={openPalette}
        >
          <i className='bi bi-search' aria-hidden='true' />
          <span className='opx-appbar__gotolabel'>Ir para…</span>
          <kbd className='opx-kbd'>{shortcutLabel(mac)}</kbd>
        </button>
        <NotificationBell />
        <UserMenu />
      </div>

      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </header>
  );
};

export default AppHeader;
